import Button, { ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import XeroLogo from './XeroLogo.svg';

// Branding-visible styles (font, colours, border, sizing) are intentionally hardcoded
// to match the official Xero SSO widget (https://developer.xero.com/documentation/xero-app-store/app-partner-guides/sign-in/).
// Values are extracted from Xero's xero-sso.js widget and must not use VerifiMe
// design tokens. Internal elements (e.g. loading spinner) may use theme tokens.
export type SignInWithXeroButtonProps = {
  loading?: boolean;
} & Omit<ButtonProps, 'variant' | 'size' | 'startIcon' | 'children'>;

export default function SignInWithXeroButton({ loading = false, sx, ...props }: SignInWithXeroButtonProps) {
  const theme = useTheme();

  return (
    <Button
      variant="outlined"
      size="large"
      startIcon={
        loading ? (
          <CircularProgress size={theme.semantic.fontSize.base} color="inherit" />
        ) : (
          <XeroLogo width={28} height={28} />
        )
      }
      {...props}
      disabled={loading || props.disabled}
      data-action="sign-in-with-xero"
      sx={[
        (theme) => ({
          backgroundColor: theme.palette.mode === 'dark' ? '#003c64' : '#ffffff',
          borderColor: theme.palette.mode === 'dark' ? '#003c64' : '#ccccd2',
          color: theme.palette.mode === 'dark' ? '#ffffff' : '#404756',
          textTransform: 'none' as const,
          fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
          fontSize: '13px',
          fontWeight: 700,
          borderRadius: '3px',
          height: '35px',
          px: '16px',
          minWidth: 0,
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? '#004e82' : '#f5f5f5',
            borderColor: theme.palette.mode === 'dark' ? '#004e82' : '#b0b3ba',
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {loading ? 'Redirecting...' : 'Sign In with Xero'}
    </Button>
  );
}
